<!--
LocationSelect Component - Reusable Location Selection Component

USE CASES:
==========
- Use in any form where location selection is needed
- Automatically loads locations from the permissions service based on franchise ID
- Supports v-model binding for location ID and type
- Handles location change callbacks
- Auto-selects first available location when franchise changes
- Parses location data into separate ID and type fields

PROPS:
=======
- modelValue: Current selected location value (format: "locationId-locationType")
- franchiseId: Franchise ID to load locations for (required)
- elemId: Unique identifier for the select element
- label: Label text for the select (defaults to 'Localidade')
- placeholder: Placeholder text (defaults to 'Clique para escolher')
- callback: Optional function to call when location changes
- disabled: Boolean to disable the select
- isReady: Boolean to control when component starts listening for franchise changes

EVENTS:
========
- update:modelValue: Emitted when location selection changes
- location-change: Emitted with { locationId, locationType } when selection changes
-->

<template>
  <select-list
    :label="label"
    :elem-id="elemId"
    :itens-select="locations"
    :model-value="selectedValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :auto-select-first="autoSelectFirst"
    :data-testid="`${elemId}-select`"
    @update:model-value="handleValueChange"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../utils/DebugLogger';
import { permissionsService } from '../services/permissionsService';
import SelectList from './SelectList.vue';

// Props
const props = defineProps({
  // Selected location ID
  locationId: {
    type: [String, Number],
    default: ''
  },

  // Selected location type
  locationType: {
    type: String,
    default: ''
  },

  // Franchise ID to load locations for
  franchiseId: {
    type: [String, Number],
    default: null
  },

  // Unique identifier for the select element
  elemId: {
    type: String,
    default: 'location-select'
  },

  // Label text for the select
  label: {
    type: String,
    default: ''
  },

  // Placeholder text
  placeholder: {
    type: String,
    default: ''
  },

  // Whether the select is disabled
  disabled: {
    type: Boolean,
    default: false
  },

  // Whether the component should listen for franchise changes
  isReady: {
    type: Boolean,
    default: false
  },

  // Whether to auto-select the first available location
  autoSelectFirst: {
    type: Boolean,
    default: true
  }
});

// Emits
const emit = defineEmits<{
  'update:locationId': [value: string | number];
  'update:locationType': [value: string];
}>();

// Composables
const { t } = useI18n();

// Reactive data
const locations = ref<any[]>([]);
const isLoading = ref<boolean>(false);
const selectedValue = ref<string>('');
const isInitialized = ref<boolean>(false);
const isReadyToListen = ref<boolean>(false);

// Computed
const computedLabel = computed((): string => {
  return props.label || t('Localidade');
});

const computedPlaceholder = computed((): string => {
  return props.placeholder || t('Clique para escolher');
});

// Methods
const loadLocations = async (franquiaId: string | number, shouldPreserveExisting = false): Promise<void> => {
  log('LocationSelect', 'loadLocations', `CALLED - franchise: ${franquiaId}, preserve: ${shouldPreserveExisting}, autoSelectFirst prop: ${props.autoSelectFirst}`);

  isLoading.value = true;
  
  try {
    if (franquiaId) {
      locations.value = permissionsService.getLocationsSelectArrayByFranquiaID(Number(franquiaId));
      
      if (locations.value.length > 0) {
        if (shouldPreserveExisting && props.locationId && props.locationType) {
          // Preserve existing selection (EDIT mode)
          const currentCombined = buildCombinedValue(String(props.locationId), props.locationType);
          selectedValue.value = currentCombined;
          log('LocationSelect', 'loadLocations', 'Preserved existing location');
        } else if (props.autoSelectFirst) {
          // Auto-select first location if prop is true (default behavior for compatibility)
          const first = locations.value[0].ID;
          selectedValue.value = first;
          handleValueChange(first);
          log('LocationSelect', 'loadLocations', 'Auto-selected first location', { autoSelectFirst: props.autoSelectFirst });
        } else {
          // NO auto-select when autoSelectFirst is false - keep field empty but load options
          selectedValue.value = '';
          log('LocationSelect', 'loadLocations', 'Auto-select DISABLED, field EMPTY', { autoSelectFirst: props.autoSelectFirst, shouldPreserveExisting });
        }
      } else {
        selectedValue.value = '';
        log('LocationSelect', 'loadLocations', `No locations available for franchise ${franquiaId}`);
      }
    } else {
      locations.value = [];
      selectedValue.value = '';
    }
  } catch (error) {
    log('LocationSelect', 'loadLocations', 'Error loading locations:', error);
    locations.value = [];
    selectedValue.value = '';
  } finally {
    isLoading.value = false;
  }
};

const parseLocationValue = (value: string | number): { locationId: string; locationType: string } => {
  if (!value) {
    return { locationId: '', locationType: '' };
  }

  const valueStr = String(value);
  const parts = valueStr.split('-');
  if (parts.length >= 2) {
    return {
      locationId: parts[0],
      locationType: parts[1]
    };
  }

  return { locationId: valueStr, locationType: '' };
};

const buildCombinedValue = (id: string | number, type: string): string => {
  if (!id && !type) return '';
  if (id && type) return `${id}-${type}`;
  return `${id || ''}`;
};

const handleValueChange = (value: string | number): void => {
  log('LocationSelect', 'handleValueChange', 'CALLED', {
    value,
    locationId: props.locationId,
    locationType: props.locationType,
    franchiseId: props.franchiseId,
    autoSelectFirst: props.autoSelectFirst,
    isReady: props.isReady
  });
  log('LocationSelect', 'handleValueChange', 'Value changed to:', value);
  selectedValue.value = String(value);
  const { locationId, locationType } = parseLocationValue(String(value));
  emit('update:locationId', locationId);
  emit('update:locationType', locationType);
};

// Watchers
watch(() => props.franchiseId, (newValue, oldValue) => {
  log('LocationSelect', 'watch:franchiseId', 'TRIGGERED', {
    newValue,
    oldValue,
    isReadyToListen: isReadyToListen.value,
    autoSelectFirst: props.autoSelectFirst,
    locationId: props.locationId,
    locationType: props.locationType
  });

  // Only react to franchise changes if we're ready to listen
  if (!isReadyToListen.value) {
    log('LocationSelect', 'watch:franchiseId', 'IGNORADO (not ready to listen)');
    return;
  }

  log('LocationSelect', 'watch:franchiseId', `Franchise changed from ${oldValue} to ${newValue}`);

  // Only preserve existing selection on the very first mount when no oldValue exists AND
  // both locationId and locationType props are explicitly provided
  const shouldPreserveExisting = !oldValue && !!(props.locationId && props.locationType);

  log('LocationSelect', 'watch:franchiseId', 'Calling loadLocations', { shouldPreserveExisting });
  loadLocations(newValue, shouldPreserveExisting);
}, { immediate: true });

// Watch for the ready state from parent
watch(() => props.isReady, (newValue, oldValue) => {
  if (newValue && props.franchiseId) {
    log('LocationSelect', 'watch:isReady', 'Parent signaled ready');
    isReadyToListen.value = true;
    
    const shouldPreserveExisting = !!(props.locationId && props.locationType);
    loadLocations(props.franchiseId, shouldPreserveExisting);
  }
});

// Keep internal combined value in sync when external props change
watch(() => [props.locationId, props.locationType], (newVals, oldVals) => {
  const combined = buildCombinedValue(String(newVals[0]), newVals[1] as string);
  
  // Only sync if this is not the initial load and the combined value is different
  if (isInitialized.value && oldVals && combined !== selectedValue.value && !isLoading.value) {
    log('LocationSelect', 'watch:locationProps', 'Syncing with external props');
    selectedValue.value = combined;
  }
}, { flush: 'post' });

// Lifecycle
onMounted(async () => {
  log('LocationSelect', 'mounted', 'Component mounted');
  
  // Mark as initialized after mount
  isInitialized.value = true;
});
</script>

<style scoped>
/* No additional styles needed - inherits from SelectList */
</style>
